/*
  HOTSTOP ISSUE 21B89 — MEDIA LOADER COMPLETION GUARD FIX

  Reusable API:
    window.HotStopMediaReveal.reveal(container, options)
    window.HotStopMediaReveal.scan(root, options)
    window.HotStopMediaReveal.reset(container, options)

  Fixes the B88 issue where the quick-add modal loader could be restarted
  repeatedly and never reveal the image.
*/

(() => {
  const existing = window.HotStopMediaReveal;

  const STATE_LOADING = 'hotstop-media-reveal--loading';
  const STATE_LOADED = 'hotstop-media-reveal--loaded';
  const STATE_FAILED = 'hotstop-media-reveal--failed';

  const DEFAULTS = {
    minDuration: 1000,
    maxDuration: 2600,
    mediaSelector: 'img, picture img, media-gallery img, video',
    once: false,
    force: false,
  };

  const now = () => performance.now();

  function toInt(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function getCssMs(container, propertyName, fallback) {
    const computed = window.getComputedStyle(container);
    const cssValue = computed.getPropertyValue(propertyName).trim();

    if (cssValue.endsWith('ms')) return toInt(cssValue, fallback);
    if (cssValue.endsWith('s')) return Math.round(Number.parseFloat(cssValue) * 1000);

    return fallback;
  }

  function getMinDuration(container, options) {
    if (options?.minDuration != null) return toInt(options.minDuration, DEFAULTS.minDuration);
    if (container?.dataset?.hotstopMediaRevealMinDuration) {
      return toInt(container.dataset.hotstopMediaRevealMinDuration, DEFAULTS.minDuration);
    }
    return getCssMs(container, '--hotstop-media-reveal-min-duration', DEFAULTS.minDuration);
  }

  function getMaxDuration(container, options) {
    if (options?.maxDuration != null) return toInt(options.maxDuration, DEFAULTS.maxDuration);
    if (container?.dataset?.hotstopMediaRevealMaxDuration) {
      return toInt(container.dataset.hotstopMediaRevealMaxDuration, DEFAULTS.maxDuration);
    }
    return getCssMs(container, '--hotstop-media-reveal-max-duration', DEFAULTS.maxDuration);
  }

  function getMedia(container, options) {
    const selector = options?.mediaSelector || container?.dataset?.hotstopMediaRevealMediaSelector || DEFAULTS.mediaSelector;
    return Array.from(container.querySelectorAll(selector))
      .filter((media) => media instanceof HTMLImageElement || media instanceof HTMLVideoElement);
  }

  function mediaSignature(container, mediaItems) {
    if (!mediaItems.length) return `empty:${container.textContent?.trim()?.length || 0}`;

    return mediaItems.map((media) => {
      if (media instanceof HTMLImageElement) {
        return [
          media.currentSrc || media.src || '',
          media.srcset || '',
          media.getAttribute('alt') || '',
          media.naturalWidth || 0,
          media.naturalHeight || 0,
        ].join('|');
      }

      if (media instanceof HTMLVideoElement) {
        return [
          media.currentSrc || media.src || '',
          media.readyState || 0,
        ].join('|');
      }

      return String(media);
    }).join('::');
  }

  function isMediaReady(media) {
    if (media instanceof HTMLImageElement) {
      return media.complete && media.naturalWidth > 0;
    }

    if (media instanceof HTMLVideoElement) {
      return media.readyState >= 2;
    }

    return true;
  }

  function waitForMedia(media, maxDuration) {
    return new Promise((resolve) => {
      if (isMediaReady(media)) {
        resolve(true);
        return;
      }

      let timeout = null;

      const done = () => {
        cleanup();
        resolve(true);
      };

      const fail = () => {
        cleanup();
        resolve(false);
      };

      const fallback = () => {
        cleanup();
        // Fallback should reveal rather than trap the UI forever.
        resolve(true);
      };

      const cleanup = () => {
        window.clearTimeout(timeout);
        media.removeEventListener('load', done);
        media.removeEventListener('loadeddata', done);
        media.removeEventListener('error', fail);
      };

      media.addEventListener('load', done, { once: true });
      media.addEventListener('loadeddata', done, { once: true });
      media.addEventListener('error', fail, { once: true });
      timeout = window.setTimeout(fallback, Math.max(900, maxDuration));
    });
  }

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, Math.max(0, ms)));
  }

  function complete(container, state = STATE_LOADED) {
    container.classList.remove(STATE_LOADING, STATE_LOADED, STATE_FAILED);
    container.classList.add(state);
    container.dataset.hotstopMediaRevealDone = 'true';
    container.dataset.hotstopMediaRevealBusy = 'false';
  }

  async function reveal(container, options = {}) {
    if (!container || !(container instanceof Element)) return null;

    const resolvedOptions = { ...DEFAULTS, ...options };
    container.setAttribute('data-hotstop-media-reveal', container.getAttribute('data-hotstop-media-reveal') || 'true');

    const mediaItems = getMedia(container, resolvedOptions);
    const signature = mediaSignature(container, mediaItems);

    // Guard: do not restart the loader for the same image/media signature.
    if (
      !resolvedOptions.force &&
      container.dataset.hotstopMediaRevealSignature === signature &&
      container.dataset.hotstopMediaRevealDone === 'true'
    ) {
      complete(container, STATE_LOADED);
      return container;
    }

    // Guard: if a reveal is already running for this same signature, do not reset it.
    if (
      !resolvedOptions.force &&
      container.dataset.hotstopMediaRevealBusy === 'true' &&
      container.dataset.hotstopMediaRevealSignature === signature
    ) {
      return container;
    }

    container.dataset.hotstopMediaRevealSignature = signature;
    container.dataset.hotstopMediaRevealBusy = 'true';
    container.dataset.hotstopMediaRevealDone = 'false';

    container.classList.remove(STATE_LOADED, STATE_FAILED);
    container.classList.add(STATE_LOADING);

    const startedAt = now();
    const minDuration = getMinDuration(container, resolvedOptions);
    const maxDuration = getMaxDuration(container, resolvedOptions);

    if (!mediaItems.length) {
      await wait(minDuration);
      complete(container, STATE_LOADED);
      return container;
    }

    const mediaPromise = Promise.all(mediaItems.map((media) => waitForMedia(media, maxDuration)));
    const fallbackPromise = wait(maxDuration).then(() => mediaItems.map(() => true));

    const results = await Promise.race([mediaPromise, fallbackPromise]);
    const elapsed = now() - startedAt;
    await wait(minDuration - elapsed);

    complete(container, results.every(Boolean) ? STATE_LOADED : STATE_FAILED);
    return container;
  }

  function scan(root = document, options = {}) {
    const scope = root instanceof Element || root === document ? root : document;
    const containers = Array.from(scope.querySelectorAll('[data-hotstop-media-reveal]'));

    if (scope instanceof Element && scope.matches('[data-hotstop-media-reveal]')) {
      containers.unshift(scope);
    }

    containers.forEach((container) => reveal(container, options));
    return containers;
  }

  function reset(container, options = {}) {
    if (!container || !(container instanceof Element)) return;

    container.classList.remove(STATE_LOADING, STATE_LOADED, STATE_FAILED);
    container.dataset.hotstopMediaRevealBusy = 'false';

    if (options.clearSignature) {
      delete container.dataset.hotstopMediaRevealSignature;
      delete container.dataset.hotstopMediaRevealDone;
    }
  }

  window.HotStopMediaReveal = {
    reveal,
    scan,
    reset,
    states: {
      loading: STATE_LOADING,
      loaded: STATE_LOADED,
      failed: STATE_FAILED,
    },
    previous: existing || null,
  };

  document.addEventListener('DOMContentLoaded', () => scan(document, { once: true }));
  document.addEventListener('shopify:section:load', (event) => scan(event.target, { once: true }));
})();
